import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Box, Button, Typography, styled, Grid } from '@mui/material';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { SectionMain } from 'layouts/ScreenLayout/sections/SectionMain';
import { MuiTableComponent } from 'screens/common/CustomUI';
import { parseName } from 'utilities/Utilities';
import { StatusColumn } from '../MemberList/components/StatusColumn';
import { homeScreenButtonText } from './constants/DashBoardConstant';

// ** Styled Components//
export const TableRowText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontFamily: 'Work Sans',
}));

// ** Styled Tooltip Component
const BootstrapTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
  },
}));

/**
 *  This component Helps in Rendering the Doctor's Dashboard Table
 * @param {Object} param0 - Contains session data, navigate function, and other props
 *   @param {Array} sessionData - Array of session data for the doctor
 *   @param {Function} navigate - Function to navigate to a different route
 *   @param {Array} upcomingSessionInWaitroom - Array of upcoming sessions in the waitroom
 * @returns {JSX.Element} - Rendered DoctorDashboardTable component
 * @description This component displays a table with session details, allowing doctors to manage their appointments and initiate calls.
 */

const DoctorDashboardTable = ({
  sessionData,
  navigate,
  upcomingSessionInWaitroom,
  videoCall,
  completeSession,
  audioCall,
}) => {
  //Data From Redux Store

  const isLoading = useSelector((state) => state.appointments.loading);
  const dispatch = useDispatch();
  const WaitingRoom = useSelector(
    (state) => state.appointments?.patientDetails?.patientAppointment?.appointmentStatus,
  );

  //Columns for the Doctor's Dashboard Table
  const column = [
    {
      field: 'time',
      headerName: 'Time',
      width: 160,
      filterable: false,
      sortable: false,
      type: 'dateTime',
      renderCell: (params) => (
        <TableRowText variant="body2">
          {' '}
          {`${moment(new Date(params.row.scheduledStartTime)).format('LT')} -
          ${moment(new Date(params.row.scheduledEndTime)).format('LT')}`}
        </TableRowText>
      ),
    },
    {
      field: 'name',
      headerName: 'Patient',
      width: 140,
      editable: false,
      type: 'string',
      renderCell: (params) => (
        <TableRowText
          variant="body2"
          onClick={() =>
            navigate(
              params.row.patient.patientId,
              params.row.appointmentId,
              params.row.appointmentStatusId,
            )
          }
        >
          {parseName(params.row.patient.firstName, params.row.patient.lastName)}
        </TableRowText>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 140,
      editable: false,
      type: 'string',
      renderCell: (params) => (
        <BootstrapTooltip title="Copy to clipboard" arrow>
          <Box
            onClick={() => {
              navigator.clipboard.writeText(params.row.patient.phone);
            }}
            style={{ cursor: 'pointer' }}
          >
            <TableRowText variant="body2">{params.row.patient.phone}</TableRowText>
          </Box>
        </BootstrapTooltip>
      ),
    },

    {
      field: 'email',
      headerName: 'Email',
      width: 200,
      editable: false,
      type: 'string',
      renderCell: (params) => (
        <BootstrapTooltip title="Copy to clipboard" arrow>
          <Box
            onClick={() => {
              navigator.clipboard.writeText(params.row.patient.email);
            }}
            style={{ cursor: 'pointer' }}
          >
            <TableRowText variant="body2">{params.row.patient.email}</TableRowText>
          </Box>
        </BootstrapTooltip>
      ),
    },

    {
      field: 'program',
      headerName: 'Program',
      width: 180,
      type: 'singleSelect',
      renderCell: (params) => (
        <TableRowText variant="body2">{params.row.program || '--'}</TableRowText>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 140,
      type: 'singleSelect',
      renderCell: (params) => (
        <TableRowText variant="body2">{params?.row?.visitType || '--'}</TableRowText>
      ),
    },
    {
      field: 'appointmentChannelType',
      headerName: 'Type',
      width: 140,
      type: 'singleSelect',
      renderCell: (params) => (
        <TableRowText variant="body2">
          {params?.row?.appointmentChannelType.split(' ')[0] || '--'}
        </TableRowText>
      ),
    },

    {
      field: 'appointmentStatus',
      headerName: 'Status',
      width: 560,
      type: 'singleSelect',
      renderCell: (params) => (
        <>
          <Box style={{ marginRight: '5px' }}>
            <TableRowText variant="body2">
              {upcomingSessionInWaitroom.length > 0 &&
              upcomingSessionInWaitroom[0]?.id === params.row.id ? (
                <StatusColumn data={upcomingSessionInWaitroom[0]?.title} />
              ) : (
                <StatusColumn data={params.row.appointmentStatus || WaitingRoom} />
              )}
            </TableRowText>
          </Box>
          <Box>
            <Button
              variant="contained"
              color="primary"
              style={{ backgroundColor: '#6387C5', color: 'white', border: 'none' }}
              // disabled={isTeleconnectStarted || isFloatingWindow}
              onClick={() =>
                window.open(
                  'https://dialer.doximity.com/dialer/home',
                  '_blank',
                  'noopener,noreferrer',
                )
              }
            >
              {homeScreenButtonText.START_SESSION}
            </Button>
          </Box>
        </>
      ),
    },
  ];
  return (
    <>
      <SectionMain
        headerElement={{
          headerText: 'Todays Sessions',
        }}
        bodyElement={[
          {
            childElements: (
              <>
                {!isLoading && sessionData.length === 0 ? (
                  <Typography variant="body2">No session scheduled for today</Typography>
                ) : (
                  <Grid container>
                    <Grid item xs={12}>
                      <MuiTableComponent
                        getRowId={(row) => row.appointmentId}
                        columns={column}
                        rows={sessionData}
                      />
                    </Grid>
                  </Grid>
                )}
              </>
            ),
          },
        ]}
      />
    </>
  );
};

export default DoctorDashboardTable;
